import { Component, inject, input, OnInit, signal } from '@angular/core';
import { form, maxLength } from '@angular/forms/signals';
import { LocationProfileService } from '../../../service/location-profile.service';
import { NotifyService } from '../../../service/notify.service';
import { Branch, LocationResponse } from '../../../types';
import { LocationProfileKeystore } from '../../../types/location-profile';

type ProfileTab = 'Account Details' | 'Location Information' | 'Shrink';

@Component({
  selector: 'app-location-profile',
  templateUrl: './location-profile.component.html',
  styleUrls: ['./location-profile.component.scss'],
  standalone: false,
  providers: [],
})
export class LocationProfileComponent implements OnInit {
  private locationProfileService = inject(LocationProfileService);
  private notifyService = inject(NotifyService);

  public isLoading = signal<boolean>(false);

  public branchId = input<string>('');
  public branchData = input<Branch>();
  public locationId = input<string>('');
  public locationData = input<LocationResponse>();

  public readonly tabs: ProfileTab[] = ['Account Details', 'Location Information', 'Shrink'];
  public currentTab = signal<ProfileTab>('Account Details');

  public profileModel = signal<LocationProfileKeystore>({
    organization: '',
    sign_up_reason: '',
    pain_points: '',
    goal: '',
    usual_theft_type: '',
    kiosk_type: '',
    kiosk_count: 0,
    workplace_type: '',
    notes: '',
    collects_shrink: false,
    shrink_amount: 0,
    sends_shrink_report: false,
    shrink_trend_direction: '',
    lp_process: '',
    restitution_policy: '',
    hr_policy: '',
    inventory_audit_frequency: '',
  });

  public profileForm = form(this.profileModel, (schema) => {
    maxLength(schema.organization, 100);
    maxLength(schema.sign_up_reason, 500);
    maxLength(schema.pain_points, 500);
    maxLength(schema.goal, 500);
    maxLength(schema.usual_theft_type, 100);
    maxLength(schema.kiosk_type, 100);
    maxLength(schema.workplace_type, 100);
    maxLength(schema.notes, 1000);
    maxLength(schema.shrink_trend_direction, 100);
    maxLength(schema.lp_process, 1000);
    maxLength(schema.restitution_policy, 1000);
    maxLength(schema.hr_policy, 1000);
    maxLength(schema.blockers, 1000);
    maxLength(schema.inventory_audit_frequency, 100);
  });

  ngOnInit() {
    this.loadLocationProfile();
  }

  loadLocationProfile() {
    this.isLoading.set(true);
    this.locationProfileService.getLocationProfile(+this.locationId()).subscribe({
      next: (profile) => {
        this.setProfileData(profile.keystore);
      },
      complete: () => {
        this.isLoading.set(false);
      },
    });
  }

  downloadProfile() {
    const permissions = this.profileModel();

    const text = JSON.stringify(permissions);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([text], { type: `application/json` }));
    a.download = `${this.locationData()?.name?.replace(/ /g, '') ?? 'null'}-${Date.now()}.panoptyclocationprofile.json`;
    a.click();

    this.notifyService.notify(`Downloaded profile for this location!`);
  }

  uploadProfile($event: any) {
    const selectedFile = $event.target.files[0];
    const fileReader = new FileReader();
    fileReader.readAsText(selectedFile, 'UTF-8');
    fileReader.onload = () => {
      const newProfile = JSON.parse(fileReader.result as string);
      this.setProfileData(newProfile);
      this.saveProfile();

      $event.target.value = '';
      this.notifyService.notify(`Imported profile into this location!`);
    };
    fileReader.onerror = (error) => {
      this.notifyService.notify(`Invalid profile file: ${error}`);
      throw error;
    };
  }

  saveProfile() {
    const profileData = this.profileModel();

    this.locationProfileService.patchLocationProfile(+this.locationId(), profileData).subscribe({
      next: (profile) => {
        this.setProfileData(profile.keystore);
        this.notifyService.notify(`Saved profile for this location!`);
      },
      error: (error) => {
        this.notifyService.notify(`Failed to save profile: ${error.message}`);
      },
    });
  }

  setProfileData(data: LocationProfileKeystore) {
    this.profileModel.update((val) => Object.assign({}, val, data));
  }
}
